import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface WizardNavigationState {
  targetRequirementId: string | null;
  navigationSource: 'unresolved_action' | null;
  focusOptions: {
    highlight: boolean;
    showMessage: boolean;
    autoScroll: boolean;
  };
}

interface WizardNavigationContextType {
  navigationState: WizardNavigationState;
  setTargetRequirement: (
    requirementId: string,
    source: 'unresolved_action',
    options?: Partial<WizardNavigationState['focusOptions']>
  ) => void;
  clearTarget: () => void;
  isNavigationPending: boolean;
}

const initialState: WizardNavigationState = {
  targetRequirementId: null,
  navigationSource: null,
  focusOptions: {
    highlight: true,
    showMessage: true,
    autoScroll: true,
  },
};

const WizardNavigationContext = createContext<WizardNavigationContextType | undefined>(undefined);

export const WizardNavigationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [navigationState, setNavigationState] = useState<WizardNavigationState>(initialState);

  const setTargetRequirement = useCallback((
    requirementId: string,
    source: 'unresolved_action',
    options: Partial<WizardNavigationState['focusOptions']> = {}
  ) => {
    setNavigationState({
      targetRequirementId: requirementId,
      navigationSource: source,
      focusOptions: {
        ...initialState.focusOptions,
        ...options,
      },
    });
  }, []);

  const clearTarget = useCallback(() => {
    setNavigationState(initialState);
  }, []);

  const isNavigationPending = navigationState.targetRequirementId !== null;

  return (
    <WizardNavigationContext.Provider
      value={{
        navigationState,
        setTargetRequirement,
        clearTarget,
        isNavigationPending,
      }}
    >
      {children}
    </WizardNavigationContext.Provider>
  );
};

export const useWizardNavigation = (): WizardNavigationContextType => {
  const context = useContext(WizardNavigationContext);
  if (context === undefined) {
    throw new Error('useWizardNavigation must be used within a WizardNavigationProvider');
  }
  return context;
};

export default WizardNavigationContext;